export type TranscriptArtifactSource = {
  id?: unknown;
  role?: unknown;
  text?: unknown;
  firstReceivedTime?: unknown;
  streamTimestamp?: unknown;
};

export type SessionArtifactMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  firstReceivedTime: number;
};

function getFiniteTimestamp(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function getStableTextHash(text: string): string {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function normalizeArtifactMessage(
  source: TranscriptArtifactSource,
  excludeActiveUser: boolean
): SessionArtifactMessage | null {
  const text = typeof source.text === "string" ? source.text.trim() : "";
  const role = source.role === "user" || source.role === "assistant" ? source.role : null;
  const sourceId = typeof source.id === "string" ? source.id.trim() : "";
  const firstReceivedTime =
    getFiniteTimestamp(source.firstReceivedTime) ?? getFiniteTimestamp(source.streamTimestamp);

  if (!text || !role || firstReceivedTime === null) return null;
  if (excludeActiveUser && sourceId.startsWith("active_user_")) return null;

  return {
    id: sourceId || `${role}_${firstReceivedTime}_${getStableTextHash(text)}`,
    role,
    text,
    firstReceivedTime,
  };
}

export function buildSessionArtifactMessages(
  voiceMessages: TranscriptArtifactSource[],
  typedMessages: TranscriptArtifactSource[]
): SessionArtifactMessage[] {
  const merged = [
    ...voiceMessages.map((message) => normalizeArtifactMessage(message, true)),
    ...typedMessages.map((message) => normalizeArtifactMessage(message, false)),
  ]
    .filter((message): message is SessionArtifactMessage => message !== null)
    .sort((left, right) => left.firstReceivedTime - right.firstReceivedTime);

  const seen = new Set<string>();
  return merged.filter((message) => {
    const key = `${message.role}:${message.text}:${message.firstReceivedTime}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
